import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { createUserWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { auth } from '../firebase/config';
import { API_ENDPOINTS, API_BASE_URL, apiClient } from '../config/api';
import './RegisterPage.css';
import stateCityData from '../data/state_city.json';
import CitySelect from '../components/common/CitySelect';
import AddressAutocomplete from '../components/common/AddressAutocomplete';
import { SERVICE_TYPE_OPTIONS } from '../data/serviceTypes';
import { formatUSPhoneInput, getUSPhoneError, toE164US } from '../utils/phone';
import { getDefaultStateFromDeviceZone } from '../utils/dateTime';

// Validate zip code — matches mobile: 4 to 8 numeric digits after stripping formatting
const validateZipCode = (zipCode) => {
  const clean = zipCode.replace(/[\s-]/g, '');
  if (!/^\d{4,8}$/.test(clean)) {
    return { isValid: false, message: 'ZIP code must be 4 to 8 digits' };
  }
  return { isValid: true, message: '' };
};

// Resize an image File to at most maxWidth×maxHeight before upload — mirrors mobile 600×600 cap
const resizeImageToMax = (file, maxWidth = 600, maxHeight = 600) =>
  new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      const ratio = Math.min(maxWidth / width, maxHeight / height);
      if (ratio < 1) {
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => resolve(new File([blob], file.name, { type: 'image/jpeg' })),
        'image/jpeg',
        0.9
      );
    };
    img.src = url;
  });


// Password validation rules (same as ForgotPasswordPage)
const passwordRules = [
  { id: 'length',  label: 'At least 8 characters',  test: (p) => p.length >= 8 },
  { id: 'upper',   label: 'One uppercase letter',    test: (p) => /[A-Z]/.test(p) },
  { id: 'lower',   label: 'One lowercase letter',    test: (p) => /[a-z]/.test(p) },
  { id: 'number',  label: 'One number',              test: (p) => /[0-9]/.test(p) },
  { id: 'special', label: 'One special character',   test: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p) },
];

const isPasswordValid = (pwd) => passwordRules.every(r => r.test(pwd));

// Mirrors mobile isBrokerType — groups BROKER, BROKER_MANAGER, BROKER_ADMIN for shared form/validation
const isBrokerType = (role) => ['broker', 'broker_admin', 'broker_manager'].includes((role || '').toLowerCase());

// Service Provider / Vendor roles that need extra SP-specific fields
const isSpType = (role) => ['service_provider', 'vendor'].includes((role || '').toLowerCase());

// Company-vendorType SP registration: Business Name replaces first/last name,
// plus a Company Logo upload. Individual vendor (default) is unchanged.
const isCompanyVendorType = (role, vendorType) => isSpType(role) && (vendorType || '').toUpperCase() === 'COMPANY';

// Invite Someone sends the vendor's chosen service type as its catalog id (e.g.
// "mortgage_broker"), while the picker here (and the registration payload) works off
// display names ("Mortgage Broker") — same convention as SpProfilePage. Resolve an
// incoming id to its name so the invite's selection shows up pre-selected; if the
// value doesn't match any known id, assume it's already a name (older/legacy links).
const resolveServiceTypeName = (value) => {
  if (!value) return '';
  const match = SERVICE_TYPE_OPTIONS.find(opt => opt.id === value || opt.name === value);
  return match ? match.name : value;
};


const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
    const user = localStorage.getItem('user');
    if (isAuthenticated && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  // Get initial form data from location.state or sessionStorage
  const getInitialFormData = () => {
    // Get agent data - prioritize sessionStorage as it's set before navigation
    let agentData = null;
    const storedAgentData = sessionStorage.getItem('agentVerificationData');
    if (storedAgentData) {
      agentData = JSON.parse(storedAgentData);
      console.log('Register Page - Found agentData in sessionStorage:', agentData);
      console.log('Register Page - sessionStorage email:', agentData?.email);
      console.log('Register Page - sessionStorage phone:', agentData?.phone);
    } else if (location.state?.agentData) {
      agentData = location.state.agentData;
      console.log('Register Page - Found agentData in location.state:', agentData);
      console.log('Register Page - location.state email:', agentData?.email);
      console.log('Register Page - location.state phone:', agentData?.phone);
    }

    // Get invite data from location.state or sessionStorage
    let inviteData = {};
    if (location.state?.inviteData) {
      inviteData = location.state.inviteData;
      console.log('Register Page - Found inviteData in location.state:', inviteData);
    } else {
      try {
        const stored = sessionStorage.getItem('inviteData');
        if (stored) {
          inviteData = JSON.parse(stored);
          console.log('Register Page - Found inviteData in sessionStorage:', inviteData);
        }
      } catch (e) {
        console.warn('Register Page - Could not parse inviteData from sessionStorage:', e);
        inviteData = {};
      }
    }

    const isAgent = agentData?.role === 'agent';
    console.log('Register Page - isAgent:', isAgent, 'agentData:', agentData);

    // Default form values
    const defaultFormData = {
      role: '',
      firstName: '',
      middleName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      street: '',
      // One-time device-zone pre-fill so the state picker lands on a plausible
      // default for a cold signup (user can change before submit). Agent/invite
      // branches below overwrite this with their own resolved stateValue.
      state: getDefaultStateFromDeviceZone(),
      city: '',
      zipCode: '',
      licenseNumber: '',
      agentNumber: '',
      narId: '',
      photo: null,
      brokerageName: '',
      brokerOfRecordName: '',
      brokerLicenseNumber: '',
      brokerageAddress: '',
      brokerageNarId: '',
      licenseType: '',
      brokerageLicenceId: '',
      // Broker's OWN verified personal DRE licence number, captured from the
      // BrokerVerificationPage licenseNumber field. Never shown as an editable/visible
      // form field on this page — it's only ever included in the register payload
      // (mirrors mobile brokerLicenseFromVerification → brokerLicenseNumber).
      brokerVerifiedLicenseNumber: '',
      // State the license was verified in — kept separate from the editable
      // mailing-address "state" so editing the address afterward can't
      // corrupt the licenceState sent at registration (mirrors mobile
      // licenseStateFromVerification).
      licenseStateFromVerification: '',
      // City the license is registered under, per the state registry — only
      // populated when the registry's own mailing-address state matches the
      // licensing jurisdiction (see AgentVerificationPage/BrokerVerificationPage
      // licenseCityFromVerification). Otherwise the licensee must type it in;
      // never silently trusts an out-of-state mailing city.
      licenseCityFromVerification: '',
      // Same trust rule as licenseCityFromVerification, for Zip Code.
      licenseZipFromVerification: '',
      // Per-field locks for agent address fields auto-filled from license
      // verification (mirrors mobile streetLocked/stateLocked/cityLocked/zipLocked).
      agentStreetLocked: false,
      agentStateLocked: false,
      agentCityLocked: false,
      agentZipLocked: false,
      // True when a BROKER arrived via a Designated-Broker/join-brokerage invite —
      // the firm identity is pinned by the invite and can't be redirected
      // (mirrors mobile brokerageLockedFromInvite).
      brokerageLockedFromInvite: false,
      isDesignatedBrokerInvite: false,
      // SP / Vendor fields
      spServiceTypes: [],
      spServiceArea: '',
      spYearsOfExperience: '',
      spLicenseNumber: '',
      spLicenseState: '',
      // Vendor Type (Individual/Company) — Company uses spBusinessName in
      // place of firstName/lastName; companyLogoFile is held in memory and
      // uploaded only after registration succeeds (no userId exists yet).
      spVendorType: 'INDIVIDUAL',
      spBusinessName: '',
      companyLogoFile: null,
      // Display-only context from the invite (populated when inviter is a
      // COMPANY-vendor SP). Never submitted — just used to render the
      // "Invited by [Company]" banner on this page.
      inviterCompanyName: '',
      inviterCompanyLogoUrl: '',
    };

    if (isAgent && agentData) {
      // AGENT: Get stateCode — prefer license state, fall back to brokerage state from API (mirrors mobile)
      let stateValue = agentData.stateCode || agentData.state_code || '';
      if (!stateValue) {
        const rawState = agentData.state || agentData.brokerageState || '';
        if (rawState) {
          const stateData = stateCityData.find(
            (item) => item.state === rawState || item.stateCode === rawState
          );
          stateValue = stateData ? stateData.stateCode : rawState;
        }
      }

      // Extract names
      let firstName = agentData.firstName || agentData.first_name || agentData.givenName || '';
      let lastName = agentData.lastName || agentData.last_name || agentData.familyName || agentData.surname || '';
      let middleName = agentData.middleName || agentData.middle_name || '';

      // Parse agentName if firstName/lastName not available
      const fullNameSource = agentData.agentName || agentData.agent_name || agentData.fullName || agentData.full_name || agentData.name || '';
      if (fullNameSource && (!firstName || !lastName)) {
        const nameParts = fullNameSource.trim().split(' ');
        if (nameParts.length >= 1 && !firstName) firstName = nameParts[0];
        if (nameParts.length >= 2 && !lastName) lastName = nameParts[nameParts.length - 1];
        if (nameParts.length > 2 && !middleName) middleName = nameParts.slice(1, -1).join(' ');
      }

      // Get email
      const email = agentData.email || agentData.emailAddress || agentData.email_address || agentData.agentEmail || agentData.agent_email || agentData.Email || '';

      // Get phone - show directly with country code (don't format)
      let phone = agentData.phone || agentData.phoneNumber || agentData.phone_number || agentData.contactPhone || agentData.contact_phone || agentData.mobile || agentData.mobileNumber || agentData.mobile_number || agentData.agentPhone || agentData.agent_phone || agentData.primaryPhone || agentData.primary_phone || agentData.cellPhone || agentData.cell_phone || agentData.telephone || agentData.tel || agentData.Phone || agentData.Mobile || '';
      // Keep phone as-is with country code (e.g., +15551234567)

      // Get other fields — fall back to brokerage city/zip from verification response (mirrors mobile)
      const city = agentData.city || agentData.agentCity || agentData.agent_city || agentData.brokerageCity || '';
      const zipCode = agentData.zipCode || agentData.zip_code || agentData.zip || agentData.postalCode || agentData.postal_code || agentData.agentZip || agentData.brokerageZip || '';
      const licenseNumber = agentData.licenseNumber || agentData.license_number || agentData.license || agentData.agentLicense || '';
      const narId = agentData.narId || agentData.nar_id || agentData.nrdsId || agentData.nrds_id || agentData.nar || '';

      // Pre-fill brokerage fields from verify-license affiliations response
      const brokerageName      = agentData.brokerageName          || '';
      const brokerOfRecordName = agentData.brokerOfRecordName      || '';
      const brokerLicenseNumber = agentData.brokerageLicenseNumber || '';
      const brokerageAddress   = agentData.brokerageAddress        || '';
      const brokerageNarId     = '';
      const licenseType        = agentData.licenseType             || '';

      // Agent's own street address from license verification, falling back to the
      // brokerage/firm address (mirrors mobile agentStreet || arelloBrokerageAddress)
      const street = agentData.street || agentData.agentStreet || brokerageAddress || '';
      // Personal (mailing) address state — reported exactly as the registry/ARELLO
      // gives it (agentData.agentState, which is verificationResponse.mailingState
      // when present), NOT `stateValue` (the license jurisdiction). Falls back to
      // the firm's state, then finally to the jurisdiction for non-verified/invite
      // flows where no real mailing state was ever supplied.
      const mailingStateValue = agentData.agentState || agentData.brokerageState || stateValue || '';

      // True when the agent reached this screen via license verification — mirrors
      // mobile's isAgentLicenseVerified (`!!verificationId`).
      const isAgentVerified = !!agentData.verificationId;
      // Per-field locks: only lock a field if verification actually supplied data
      // for it, mirroring mobile's streetLocked/stateLocked/cityLocked/zipLocked
      // (which key off the firm's ARELLO address, not the agent's own address).
      const agentStreetLocked = isAgentVerified && !!agentData.brokerageAddress;
      const agentStateLocked  = isAgentVerified && !!(agentData.agentState || agentData.brokerageState);
      const agentCityLocked   = isAgentVerified && !!agentData.brokerageCity;
      const agentZipLocked    = isAgentVerified && !!agentData.brokerageZip;

      console.log('Register Page - Initial form values:', { firstName, lastName, email, phone, street, brokerageName, brokerOfRecordName, brokerLicenseNumber, brokerageAddress, brokerageNarId, licenseType });

      return {
        ...defaultFormData,
        role: 'agent',
        firstName,
        middleName,
        lastName,
        email,
        phone,
        state: mailingStateValue,
        // Preserve the state where the license was verified, independent of the
        // editable mailing-address "state" above (mirrors mobile licenseStateFromVerification).
        licenseStateFromVerification: stateValue,
        licenseCityFromVerification: agentData.licenseCityFromVerification || '',
        licenseZipFromVerification: agentData.licenseZipFromVerification || '',
        street,
        city,
        zipCode,
        licenseNumber,
        narId,
        brokerageName,
        brokerOfRecordName,
        brokerLicenseNumber,
        brokerageAddress,
        brokerageNarId,
        licenseType,
        agentStreetLocked,
        agentStateLocked,
        agentCityLocked,
        agentZipLocked,
      };
    } else if (inviteData && Object.keys(inviteData).length > 0) {
      // NON-AGENT: Get stateCode
      let stateValue = inviteData.stateCode || '';
      if (!stateValue && inviteData.state) {
        const stateData = stateCityData.find(
          (item) => item.state === inviteData.state || item.stateCode === inviteData.state
        );
        stateValue = stateData ? stateData.stateCode : inviteData.state;
      }
      // Personal (mailing) address state — reported exactly as the registry/ARELLO
      // gives it (inviteData.personalState, from BrokerVerificationPage's
      // verificationResponse.mailingState), NOT `stateValue` (the license
      // jurisdiction). Falls back to the jurisdiction for non-verified/invite
      // flows where no real mailing state was ever supplied.
      const mailingStateValue = inviteData.personalState || stateValue || '';

      // Get phone - show directly with country code (don't format)
      let phone = inviteData.phone || inviteData.phoneNumber || '';

      // Locked from invite when a Designated-Broker/join-brokerage invite pins the
      // firm identity (mirrors mobile brokerageLockedFromInvite). Deliberately checks
      // only brokerageLicenseId/brokerageLicenceId, not companyName — BrokerVerificationPage
      // merges its own ARELLO-detected companyName over inviteData.companyName, so that key
      // is not a reliable signal of an actual invite (would false-positive for any standalone
      // broker whose verified license happens to have an affiliated firm on record).
      const brokerageLockedFromInvite = !!(inviteData.brokerageLicenseId || inviteData.brokerageLicenceId);

      return {
        ...defaultFormData,
        role: inviteData.role || '',
        firstName: inviteData.firstName || '',
        middleName: inviteData.middleName || '',
        lastName: inviteData.lastName || '',
        email: inviteData.email || '',
        phone,
        state: mailingStateValue,
        // State the license was verified in, kept separate from the editable
        // mailing-address state above (mirrors mobile licenseStateFromVerification).
        licenseStateFromVerification: stateValue,
        licenseCityFromVerification: inviteData.licenseCityFromVerification || '',
        licenseZipFromVerification: inviteData.licenseZipFromVerification || '',
        // Broker's own personal address from license verification, falling back to the
        // brokerage/firm address (mirrors mobile brokerStreet/City/Zip → arelloBrokerage* priority)
        street: inviteData.street || '',
        city: inviteData.city || inviteData.brokerageCity || '',
        zipCode: inviteData.zipCode || inviteData.zip || inviteData.brokerageZip || '',
        // Pre-fill broker/company fields for broker-type roles (mirrors mobile isBrokerType pre-fill)
        brokerageName: inviteData.brokerageName || inviteData.companyName || '',
        brokerOfRecordName: inviteData.brokerOfRecordName || '',
        // Editable "Company License Number" field: only pre-filled when a
        // Designated-Broker/join-brokerage invite pins a specific firm licence.
        // A standalone broker (no such invite) always starts blank — they must
        // type a firm licence number themselves to link to one.
        brokerLicenseNumber: brokerageLockedFromInvite
          ? (inviteData.brokerageLicenseId || inviteData.brokerageLicenceId || '')
          : '',
        // Broker's own verified personal DRE licence — captured silently from
        // BrokerVerificationPage, never shown as a form field (mirrors mobile
        // brokerLicenseFromVerification).
        brokerVerifiedLicenseNumber: inviteData.brokerLicenseNumber || '',
        brokerageAddress: inviteData.brokerageAddress || inviteData.companyAddress || '',
        brokerageNarId: inviteData.brokerageNarId || '',
        licenseType: inviteData.licenseType || '',
        brokerageLicenceId: inviteData.brokerageLicenseId || inviteData.brokerageLicenceId || '',
        brokerageLockedFromInvite,
        isDesignatedBrokerInvite: !!inviteData.isDesignatedBrokerInvite,
        // SP / Vendor pre-fill from invite payload — resolve each catalog id to its
        // display name so it matches an entry in the picker (see resolveServiceTypeName).
        spServiceTypes: (Array.isArray(inviteData.serviceTypes)
          ? inviteData.serviceTypes
          : (inviteData.serviceType ? [inviteData.serviceType] : [])
        ).map(resolveServiceTypeName).filter(Boolean),
        spServiceArea: inviteData.serviceArea || '',
        spYearsOfExperience: inviteData.yearsOfExperience ? String(inviteData.yearsOfExperience) : '',
        spLicenseNumber: inviteData.licenseNumber || '',
        spLicenseState: inviteData.licenseState || '',
        // Vendor Type — deliberately does NOT fall back to splitting inviteeName,
        // since for a Company invite that name IS the business name and would
        // mangle "Acme Inspections LLC" into bogus first/last name parts.
        spVendorType: (inviteData.vendorType || 'INDIVIDUAL').toUpperCase(),
        spBusinessName: inviteData.businessName || '',
        // Inviter-company display context — rendered as a banner on the form
        // header. Never submitted; see payload builders below.
        inviterCompanyName: inviteData.inviterCompanyName || '',
        inviterCompanyLogoUrl: inviteData.inviterCompanyLogoUrl || '',
      };
    }

    return defaultFormData;
  };

  const [formData, setFormData] = useState(getInitialFormData);
  const [isEmailPreFilled, setIsEmailPreFilled] = useState(() => !!getInitialFormData().email);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Service-type picker catalog — seeded with the offline fallback, overwritten by the
  // live GET /api/v1/service-types fetch below (mirrors InviteSomeonePage's picker).
  const [serviceTypeOptions, setServiceTypeOptions] = useState(SERVICE_TYPE_OPTIONS);
  // Broker license verification
  const [isBrokerVerifying, setIsBrokerVerifying] = useState(false);
  const [brokerVerificationResult, setBrokerVerificationResult] = useState(null);
  const [isBrokerVerified, setIsBrokerVerified] = useState(false);
  const brokerDebounceRef = useRef(null);
  const brokerVerificationResultRef = useRef(null);

  // Company picker — INDIVIDUAL SPs link to a Rialty COMPANY-vendor SP (parent
  // company). Picker searches by businessName / email / phone (see backend
  // /api/v1/service-provider/company/search); on pick, the selected company's
  // card renders in place of the "Find your company" affordance. The chosen
  // userId is PATCHed onto sp-profile after register succeeds.
  const [parentCompanyUserId, setParentCompanyUserId] = useState('');
  const [parentCompany, setParentCompany] = useState(null);
  const [showCompanyPickerModal, setShowCompanyPickerModal] = useState(false);
  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const [companySearchResults, setCompanySearchResults] = useState([]);
  const [companySearching, setCompanySearching] = useState(false);

  // Debounced company search — fires 300 ms after the last keystroke, only
  // while the modal is open and the query has ≥2 chars (server short-circuits
  // shorter queries to [] anyway). Mirrors SpProfilePage.js and the SP mobile
  // ViewProfileScreen pattern. Called pre-auth (no Firebase session yet), so
  // the request is sent without an Authorization header — same contract as
  // the service-types fetch above.
  useEffect(() => {
    if (!showCompanyPickerModal) return undefined;
    const trimmed = companySearchQuery.trim();
    if (trimmed.length < 2) {
      setCompanySearchResults([]);
      setCompanySearching(false);
      return undefined;
    }
    let cancelled = false;
    setCompanySearching(true);
    const timer = setTimeout(async () => {
      try {
        const resp = await apiClient.get(API_ENDPOINTS.spCompanySearch, {
          params: { q: trimmed, limit: 20 },
        });
        if (cancelled) return;
        const rows = Array.isArray(resp.data) ? resp.data : (resp.data?.content || resp.data?.data || []);
        setCompanySearchResults(Array.isArray(rows) ? rows : []);
      } catch (error) {
        if (!cancelled) {
          console.warn('[RegisterPage] Company search failed:', error);
          setCompanySearchResults([]);
        }
      } finally {
        if (!cancelled) setCompanySearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [companySearchQuery, showCompanyPickerModal]);

  const handleCompanyPick = (row) => {
    setParentCompanyUserId(row.userId);
    setParentCompany(row);
    setShowCompanyPickerModal(false);
    setCompanySearchQuery('');
    setCompanySearchResults([]);
  };

  const handleCompanyUnlink = () => {
    setParentCompanyUserId('');
    setParentCompany(null);
  };

  // Recovery effect: if email is still empty after mount, re-read from location.state or
  // sessionStorage. Handles cases where the useState initializer ran before location.state
  // was available, or where sessionStorage.getItem threw during initialisation.
  useEffect(() => {
    if (formData.email) return; // already populated — nothing to do

    let inv = null;

    // 1. Try location.state (set by InvitePage's navigate call)
    if (location.state?.inviteData) {
      inv = location.state.inviteData;
    }

    // 2. Fallback: sessionStorage
    if (!inv || !inv.email) {
      try {
        const stored = sessionStorage.getItem('inviteData');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed && parsed.email) inv = parsed;
        }
      } catch (e) {
        console.warn('[RegisterPage] sessionStorage fallback parse failed:', e);
      }
    }

    if (!inv || !inv.email) return; // nothing to populate

    setFormData(prev => ({
      ...prev,
      email:              inv.email,
      firstName:          prev.firstName          || inv.firstName          || '',
      lastName:           prev.lastName           || inv.lastName           || '',
      middleName:         prev.middleName         || inv.middleName         || '',
      phone:              prev.phone              || inv.phone              || inv.phoneNumber || '',
      role:               prev.role               || inv.role               || '',
      street:             prev.street             || inv.street             || '',
      city:               prev.city               || inv.city               || inv.brokerageCity  || '',
      zipCode:            prev.zipCode            || inv.zipCode            || inv.zip || inv.brokerageZip || '',
      licenseStateFromVerification: prev.licenseStateFromVerification || inv.stateCode || '',
      licenseCityFromVerification: prev.licenseCityFromVerification || inv.licenseCityFromVerification || '',
      licenseZipFromVerification: prev.licenseZipFromVerification || inv.licenseZipFromVerification || '',
      brokerageName:      prev.brokerageName      || inv.brokerageName      || inv.companyName          || '',
      brokerOfRecordName: prev.brokerOfRecordName || inv.brokerOfRecordName || '',
      // Blank unless an actual invite pins a firm licence — mirrors the initial-load rule above.
      brokerLicenseNumber: prev.brokerLicenseNumber || ((inv.brokerageLicenseId || inv.brokerageLicenceId) ? (inv.brokerageLicenseId || inv.brokerageLicenceId) : ''),
      brokerVerifiedLicenseNumber: prev.brokerVerifiedLicenseNumber || inv.brokerLicenseNumber || '',
      brokerageAddress:   prev.brokerageAddress   || inv.brokerageAddress   || inv.companyAddress       || '',
      brokerageNarId:     prev.brokerageNarId     || inv.brokerageNarId     || '',
      licenseType:        prev.licenseType        || inv.licenseType        || '',
      brokerageLicenceId: prev.brokerageLicenceId || inv.brokerageLicenseId || inv.brokerageLicenceId || '',
      // Only brokerageLicenseId/brokerageLicenceId are a reliable "locked from invite"
      // signal — inv.companyName can be overwritten by ARELLO-detected data (see note above).
      brokerageLockedFromInvite: prev.brokerageLockedFromInvite || !!(inv.brokerageLicenseId || inv.brokerageLicenceId),
      isDesignatedBrokerInvite:  prev.isDesignatedBrokerInvite  || !!inv.isDesignatedBrokerInvite,
      // SP / Vendor fields — resolve catalog ids to display names (see resolveServiceTypeName)
      spServiceTypes: prev.spServiceTypes?.length > 0
        ? prev.spServiceTypes
        : (Array.isArray(inv.serviceTypes) ? inv.serviceTypes : (inv.serviceType ? [inv.serviceType] : []))
            .map(resolveServiceTypeName).filter(Boolean),
      spServiceArea:       prev.spServiceArea       || inv.serviceArea                                   || '',
      spYearsOfExperience: prev.spYearsOfExperience || (inv.yearsOfExperience ? String(inv.yearsOfExperience) : ''),
      spLicenseNumber:     prev.spLicenseNumber     || inv.licenseNumber                                 || '',
      spLicenseState:      prev.spLicenseState      || inv.licenseState                                  || '',
      spVendorType:        prev.spVendorType && prev.spVendorType !== 'INDIVIDUAL'
        ? prev.spVendorType
        : (inv.vendorType || 'INDIVIDUAL').toUpperCase(),
      spBusinessName:      prev.spBusinessName      || inv.businessName                                  || '',
      inviterCompanyName:  prev.inviterCompanyName  || inv.inviterCompanyName                            || '',
      inviterCompanyLogoUrl: prev.inviterCompanyLogoUrl || inv.inviterCompanyLogoUrl                     || '',
    }));
    setIsEmailPreFilled(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch the canonical service-type catalog once on mount. The fallback list already
  // seeds the picker so it's usable while this is in flight (or if it fails); this
  // overwrites it as soon as the response lands. Unlike InviteSomeonePage, this page is
  // pre-authentication (no Firebase session yet), so the request is sent without an
  // Authorization header.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiClient.get(API_ENDPOINTS.serviceTypes);
        const catalog = Array.isArray(resp.data) ? resp.data : (resp.data?.content || resp.data?.data || []);
        if (!cancelled && catalog.length > 0) {
          setServiceTypeOptions(catalog.map(t => ({ id: t.id, name: t.label })));
        }
      } catch (error) {
        console.warn('[RegisterPage] Failed to fetch service types, using fallback:', error);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Keep broker verification ref in sync with state (avoids stale closure in debounce)
  useEffect(() => {
    brokerVerificationResultRef.current = brokerVerificationResult;
  }, [brokerVerificationResult]);

  // Look up the Brokerage/Company License Number against OUR registry (local
  // CA/CO Firestore mirror, ARELLO fallback) via the same /api/public/verify-license
  // path the agent's own license uses — role=BROKERAGE. Deliberately NOT
  // /api/public/verify-broker: that endpoint only calls ARELLO directly, it
  // never touches the local registry (BrokerLicenseVerificationService only
  // injects ArelloClient), so it wouldn't satisfy "from our registry".
  // Optional field: no match / no state to search in just leaves whatever the
  // user already typed alone — no error shown, nothing blocks registration.
  const verifyBrokerAPI = useCallback(async (licenseNumber, stateCode) => {
    if (!licenseNumber || !stateCode) return;
    setIsBrokerVerifying(true);
    setBrokerVerificationResult(null);
    setIsBrokerVerified(false);
    try {
      const response = await apiClient.post(API_ENDPOINTS.verifyLicense, {
        licenseNumber,
        state: stateCode,
        role: 'BROKERAGE',
      });
      const data = response.data?.data || response.data;
      const verified = data?.status === 'valid';
      setBrokerVerificationResult({ success: verified, data });
      setIsBrokerVerified(verified);
      if (verified) {
        // Street (when the registry has one, e.g. CA) + city/state/zip (from
        // mailingState when present — CO's own city/zip belong to the mailing
        // address, not the "state" field, which is the licensing jurisdiction;
        // pairing city/zip with the wrong state would print a nonsense address
        // like "Hendersonville, CO" for a Tennessee-based firm). CO has no
        // street-level data at all, so this becomes just "City, ST ZIP" —
        // still real, useful info, and the field stays editable so the user
        // can fill in the actual street themselves.
        const addressState = data.mailingState || data.state;
        const cityStateZip = [data.city, [addressState, data.zipCode].filter(Boolean).join(' ')]
          .filter(Boolean).join(', ');
        const fullAddress = [data.addressLine1, data.addressLine2, cityStateZip]
          .filter(Boolean).join(', ');
        setFormData(prev => {
          if (prev.brokerLicenseNumber !== licenseNumber) return prev; // stale response, field changed since
          return {
            ...prev,
            brokerageName: data.entityName || data.fullName || prev.brokerageName,
            brokerageAddress: fullAddress || prev.brokerageAddress,
            licenseType: data.licenseType || prev.licenseType,
          };
        });
      }
    } catch (error) {
      // Not found / lookup failed — optional field, leave it alone.
      setBrokerVerificationResult({ success: false });
      setIsBrokerVerified(false);
    } finally {
      setIsBrokerVerifying(false);
    }
  }, []);


  // Cleanup broker debounce timer on unmount
  useEffect(() => {
    return () => {
      if (brokerDebounceRef.current) clearTimeout(brokerDebounceRef.current);
    };
  }, []);

  const [status, setStatus] = useState({ type: '', message: '' });
  const [photoFileName, setPhotoFileName] = useState('');
  // Local blob: URL for the company-logo thumbnail — the raw File object in
  // formData.companyLogoFile can't be <img src=>'d directly. Revoked when it
  // changes (or on unmount) to avoid leaking the blob into the browser's
  // reference table.
  const [companyLogoPreview, setCompanyLogoPreview] = useState('');
  useEffect(() => {
    return () => {
      if (companyLogoPreview && companyLogoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(companyLogoPreview);
      }
    };
  }, [companyLogoPreview]);
  const [zipCodeError, setZipCodeError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [legalModal, setLegalModal] = useState(null); // 'terms' | 'privacy' | null

  // Fires the registry lookup right away on blur instead of waiting out the
  // rest of the (re)started 800ms debounce from the last keystroke — a user
  // tabbing/clicking away right after typing shouldn't have to wait idle for
  // a lookup that could start immediately. Skips firing a duplicate request
  // if one from typing is already in flight.
  const handleBrokerLicenseBlur = () => {
    if (isBrokerVerifying) return;
    const lookupState = formData.licenseStateFromVerification;
    const cleaned = formData.brokerLicenseNumber;
    if (!lookupState || !cleaned || cleaned.length < 4 || formData.brokerageLockedFromInvite) return;
    if (brokerDebounceRef.current) clearTimeout(brokerDebounceRef.current);
    verifyBrokerAPI(cleaned, lookupState);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // If state changes, reset city, zipCode, and re-trigger broker verification
    if (name === 'state') {
      setFormData({
        ...formData,
        state: value,
        city: '',
        zipCode: ''
      });
      setZipCodeError('');
    } else if (name === 'brokerLicenseNumber') {
      const cleaned = value.replace(/[^a-zA-Z0-9]/g, '');
      setFormData({ ...formData, brokerLicenseNumber: cleaned });
      setIsBrokerVerified(false);
      setBrokerVerificationResult(null);
      if (brokerDebounceRef.current) clearTimeout(brokerDebounceRef.current);
      // Only search if we know which state's registry to check — the agent's/
      // broker's own verified license state (there's no separate state picker
      // for the brokerage). Locked-from-invite fields are already read-only
      // and pre-filled, no need to re-look-up.
      const lookupState = formData.licenseStateFromVerification;
      if (lookupState && cleaned.length >= 4 && !formData.brokerageLockedFromInvite) {
        brokerDebounceRef.current = setTimeout(() => verifyBrokerAPI(cleaned, lookupState), 800);
      }
    } else if (name === 'brokerageLicenceId') {
      setFormData({ ...formData, brokerageLicenceId: value.replace(/[^a-zA-Z0-9-]/g, '') });
    } else if (name === 'licenseNumber') {
      setFormData({ ...formData, licenseNumber: value.replace(/[^a-zA-Z0-9]/g, '') });
    } else if (name === 'spLicenseNumber') {
      setFormData({ ...formData, spLicenseNumber: value.replace(/[^a-zA-Z0-9-]/g, '') });
    } else if (name === 'spYearsOfExperience') {
      setFormData({ ...formData, spYearsOfExperience: value.replace(/[^0-9]/g, '').slice(0, 2) });
    } else if (name === 'zipCode') {
      setFormData({ ...formData, zipCode: value });
      if (value.trim()) {
        const validation = validateZipCode(value);
        setZipCodeError(validation.isValid ? '' : validation.message);
      } else {
        setZipCodeError('');
      }
    } else if (name === 'phone') {
      setPhoneError('');
      setFormData({
        ...formData,
        phone: formatUSPhoneInput(value)
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // Vendor / Service Provider — toggles a service type name in/out of the
  // selected list (pill-grid picker, mirrors SpProfilePage's toggleServiceType).
  const toggleSpServiceType = (name) => {
    setFormData(prev => {
      const current = prev.spServiceTypes || [];
      return {
        ...prev,
        spServiceTypes: current.includes(name)
          ? current.filter(n => n !== name)
          : [...current, name],
      };
    });
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const resized = await resizeImageToMax(file);
      setFormData({ ...formData, photo: resized });
      setPhotoFileName(file.name);
    }
  };


  /**
   * Accept pending invite after registration — mirrors mobile's RegistrationProgressScreen.
   * Handles both invite types:
   *  - type=user  → POST /api/v1/invitations/{key}/accept  (created by InviteSomeonePage)
   *  - type=transaction → POST /api/v1/transactions/invitations/accept
   * Returns true if invite was accepted and navigation was triggered, false otherwise.
   */
  const handlePendingInvite = async (firebaseToken, userData) => {
    const pendingInvite = sessionStorage.getItem('inviteData');
    if (!pendingInvite) return false;

    let inviteInfo;
    try {
      inviteInfo = JSON.parse(pendingInvite);
    } catch (e) {
      console.error('Error parsing inviteData:', e);
      sessionStorage.removeItem('inviteData');
      return false;
    }

    if (!inviteInfo.key) return false;

    const inviteUserId = userData.id || userData.odooId || userData.firebaseUid || '';
    const inviteUserName = [userData.firstName, userData.lastName].filter(Boolean).join(' ') || userData.fullName || '';
    const authHeaders = { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' };

    try {
      let transactionId = inviteInfo.transactionId || '';

      if (inviteInfo.type === 'user') {
        // ── User invite (created by InviteSomeonePage with type=user) ──────────
        // Mirrors mobile: await authApi.post(`/api/v1/invitations/${key}/accept`)
        console.log('Accepting user invite (type=user):', inviteInfo.key);
        const acceptRes = await apiClient.post(
          API_ENDPOINTS.acceptUserInvite(inviteInfo.key),
          {},
          { headers: authHeaders }
        );
        // Try to get transactionId from response
        const resData = acceptRes.data?.data || acceptRes.data || {};
        transactionId = resData.transactionId || resData.id || transactionId;
        console.log('User invite accepted, transactionId:', transactionId);

        // Mirrors mobile addRole + fetchProfile: explicitly add the role to the
        // UserRole table (registration payload alone may not create the entry)
        const registeredRole = userData.role?.toUpperCase() || inviteInfo.role?.toUpperCase() || '';
        if (inviteUserId && registeredRole) {
          try {
            await apiClient.post(
              API_ENDPOINTS.userRoles(inviteUserId),
              { role: registeredRole },
              { headers: authHeaders }
            );
          } catch { /* role may already exist — non-critical */ }
          // Refresh profile so Switch Role reflects the correct roles array
          try {
            const profileRes = await apiClient.get(API_ENDPOINTS.userMe, { headers: authHeaders });
            const updated = profileRes.data?.data || profileRes.data?.content || profileRes.data;
            if (updated && typeof updated === 'object') {
              const pinnedRole = localStorage.getItem('currentRole') || registeredRole;
              localStorage.setItem('user', JSON.stringify({ ...userData, ...updated, role: pinnedRole }));
            }
          } catch { /* non-critical */ }
        }

        // No welcome popup for user invites — they don't carry property details
        sessionStorage.removeItem('inviteData');
        setTimeout(() => navigate('/dashboard', { replace: true }), 1500);

      } else {
        // ── Transaction invite (type=transaction) ────────────────────────────
        console.log('Accepting transaction invite (type=transaction):', inviteInfo.key);
        // Step 1: Validate to get transaction details
        const validateRes = await apiClient.get(
          API_ENDPOINTS.decodeInvite(inviteInfo.key),
          { headers: authHeaders }
        );
        const inviteDetails = validateRes.data?.data || validateRes.data || {};
        transactionId = inviteDetails.transactionId || inviteDetails.id || transactionId;

        // Step 2: Accept
        await apiClient.post(
          API_ENDPOINTS.acceptInvite,
          { invitationKey: inviteInfo.key, userId: inviteUserId, userName: inviteUserName },
          { headers: authHeaders }
        );
        console.log('Transaction invite accepted, transactionId:', transactionId);

        // Store welcome popup data for dashboard (only for transaction invites)
        const welcomeData = {
          propertyAddress: inviteInfo.propertyAddress || '',
          city: inviteInfo.city || '',
          state: inviteInfo.state || '',
          role: inviteInfo.role || '',
          transactionId
        };
        sessionStorage.setItem('welcomeTransactionData', JSON.stringify(welcomeData));
        sessionStorage.removeItem('inviteData');
        setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
      }
      return true;

    } catch (acceptErr) {
      console.error('Error accepting invite after registration:', acceptErr.response?.data || acceptErr.message);
      sessionStorage.removeItem('inviteData');
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    // Validation
    const isCompanyVendor = isCompanyVendorType(formData.role, formData.spVendorType);

    if (isCompanyVendor) {
      if (!formData.spBusinessName.trim()) {
        setStatus({ type: 'error', message: 'Please enter your business name' });
        setIsSubmitting(false);
        return;
      }
    } else {
      if (!formData.firstName.trim()) {
        setStatus({ type: 'error', message: 'Please enter your first name' });
        setIsSubmitting(false);
        return;
      }

      if (!formData.lastName.trim()) {
        setStatus({ type: 'error', message: 'Please enter your last name' });
        setIsSubmitting(false);
        return;
      }
    }

    if (!formData.email.trim()) {
      setStatus({ type: 'error', message: 'Please enter your email address' });
      setIsSubmitting(false);
      return;
    }

    const phoneValidationError = getUSPhoneError(formData.phone);
    if (phoneValidationError) {
      setStatus({ type: 'error', message: phoneValidationError });
      setIsSubmitting(false);
      return;
    }

    if (!formData.password || !isPasswordValid(formData.password)) {
      setStatus({ type: 'error', message: 'Password does not meet the requirements (8+ chars, uppercase, lowercase, number, special character)' });
      setIsSubmitting(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setStatus({ type: 'error', message: 'Passwords do not match' });
      setIsSubmitting(false);
      return;
    }

    if (!formData.street.trim()) {
      setStatus({ type: 'error', message: 'Please enter your street address' });
      setIsSubmitting(false);
      return;
    }

    if (!formData.state) {
      setStatus({ type: 'error', message: 'Please select a state' });
      setIsSubmitting(false);
      return;
    }

    if (!formData.city) {
      setStatus({ type: 'error', message: 'Please select a city' });
      setIsSubmitting(false);
      return;
    }

    if (!formData.zipCode.trim()) {
      setStatus({ type: 'error', message: 'Please enter your zip code' });
      setIsSubmitting(false);
      return;
    }

    const zipValidation = validateZipCode(formData.zipCode);
    if (!zipValidation.isValid) {
      setZipCodeError(zipValidation.message);
      setStatus({ type: 'error', message: zipValidation.message });
      setIsSubmitting(false);
      return;
    }

    // Validation for SP / Vendor fields
    if (isSpType(formData.role)) {
      if (!formData.spServiceTypes || formData.spServiceTypes.length === 0) {
        setStatus({ type: 'error', message: 'Please select at least one service type' });
        setIsSubmitting(false);
        return;
      }
      if (!formData.spServiceArea.trim()) {
        setStatus({ type: 'error', message: 'Please enter your service area' });
        setIsSubmitting(false);
        return;
      }
      if (!formData.spYearsOfExperience) {
        setStatus({ type: 'error', message: 'Please enter your years of experience' });
        setIsSubmitting(false);
        return;
      }
      if (!formData.spLicenseState) {
        setStatus({ type: 'error', message: 'Please select your license state' });
        setIsSubmitting(false);
        return;
      }
    }

    if (!agreedToTerms) {
      setStatus({ type: 'error', message: 'You must agree to the Terms of Service and Privacy Policy to register.' });
      setIsSubmitting(false);
      return;
    }

    try {
      // Step 1: Create user in Firebase
      setStatus({ type: 'info', message: 'Creating your account...' });
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      // Step 2: Get Firebase auth token
      const firebaseToken = await userCredential.user.getIdToken();
      console.log('Firebase authentication successful');

      // Step 3: Prepare data for backend API
      // (isCompanyVendor already computed during validation above, still in scope)
      const registrationData = {
        ...(isCompanyVendor ? {} : {
          firstName: formData.firstName,
          middleName: formData.middleName || null,
          lastName: formData.lastName,
        }),
        email: formData.email,
        phone: toE164US(formData.phone), // Convert to E.164 format (+1XXXXXXXXXX)
        smsConsent,
        role: formData.role?.toUpperCase() || null,
        firebaseUid: userCredential.user.uid,
        // Address fields
        street: formData.street || null,
        state: formData.state || null,
        city: formData.city || null,
        postalCode: formData.zipCode || null,
        // License/agent fields
        // Preserve the state the license was verified in — falls back to the
        // editable mailing-address state when no verification took place
        // (mirrors mobile licenseStateFromVerification || getStateCode(state))
        licenseState: formData.licenseStateFromVerification || formData.state || null,
        licenseCity: formData.licenseCityFromVerification || null,
        licenseZip: formData.licenseZipFromVerification || null,
        agentNumber: formData.licenseNumber || null,
        narId: formData.narId || null,
        // Brokerage fields
        brokerageName: formData.brokerageName || null,
        brokerOfRecordName: formData.brokerOfRecordName || null,
        // AGENT: firm's licence goes to brokerLicenseNumber (mirrors mobile — agent
        // payload has no companyLicenseNumber key at all).
        // BROKER: the broker's own verified personal DRE licence, captured silently
        // from BrokerVerificationPage — never the editable "Company License Number"
        // field (mirrors mobile brokerLicenseFromVerification → brokerLicenseNumber).
        ...(formData.role?.toLowerCase() === 'agent'
          ? { brokerLicenseNumber: formData.brokerLicenseNumber || null }
          : isBrokerType(formData.role)
            ? { brokerLicenseNumber: formData.brokerVerifiedLicenseNumber || null }
            : {}),
        // companyLicenseNumber: firm/brokerage DRE licence — BROKER only, sourced from
        // the editable "Company License Number" field (mirrors mobile brokerageLicenseNumber
        // → companyLicenseNumber; the agent registration payload never sends this key).
        companyLicenseNumber: isBrokerType(formData.role)
          ? (formData.brokerLicenseNumber || null)
          : null,
        brokerageAddress: formData.brokerageAddress || null,
        brokerageNarId: formData.brokerageNarId || null,
        // Firm's license type (e.g. "Corporation") — mobile sends this under
        // brokerageLicenseType for both agent and broker registration.
        brokerageLicenseType: formData.licenseType || null,
        brokerageLicenseId: formData.brokerageLicenceId || null,
        // SP / Vendor fields (only included for those roles)
        ...(isSpType(formData.role) ? {
          serviceTypes:      formData.spServiceTypes?.length > 0 ? formData.spServiceTypes : null,
          serviceArea:       formData.spServiceArea                                                   || null,
          yearsOfExperience: formData.spYearsOfExperience ? parseInt(formData.spYearsOfExperience, 10) : null,
          licenseNumber:     formData.spLicenseNumber                                                 || null,
          licenseState:      formData.spLicenseState || formData.state                                || null,
          vendorType:        formData.spVendorType                                                    || null,
          businessName:      isCompanyVendor ? (formData.spBusinessName || null) : null,
        } : {}),
      };
      console.log("==registrationData==",registrationData);
      // Step 4: Call Java backend API with Firebase token
      setStatus({ type: 'info', message: 'Completing registration...' });

      console.log('Calling backend API with data:', {
        url: API_ENDPOINTS.register,
        data: registrationData,
        hasToken: !!firebaseToken
      });

      let backendResponse = null;
      let userId = null;

      // Step 4a: Register user in backend
      try {
        const response = await apiClient.post(
          API_ENDPOINTS.register,
          registrationData,
          {
            headers: {
              'Authorization': `Bearer ${firebaseToken}`,
              'Content-Type': 'application/json'
            },

          }
        );

        console.log('Backend registration successful:', response.data);
        backendResponse = response.data;
        // Handle both direct { id, ... } and nested { data: { id, ... } } response formats
        const rawBackend = backendResponse;
        backendResponse = rawBackend?.id ? rawBackend : (rawBackend?.data || rawBackend);
        userId = backendResponse?.id || backendResponse?.userId || userCredential.user.uid;

      } catch (apiError) {
        console.error('Backend API Error:', apiError);
        console.error('Error details:', {
          message: apiError.message,
          code: apiError.code,
          response: apiError.response?.data,
          status: apiError.response?.status
        });

        // Check if the error means the user already exists in the database
        const isUserExistsError =
          apiError.response?.status === 409 ||
          apiError.response?.data?.message?.toLowerCase().includes('already exists') ||
          apiError.response?.data?.message?.toLowerCase().includes('already registered') ||
          apiError.response?.data?.error?.toLowerCase().includes('already exists');

        if (isUserExistsError) {
          // User already exists in the database — keep the Firebase user, let them log in
          console.log('ℹ️ User already exists in database. Keeping Firebase user.');
          userId = userCredential.user.uid;
        } else {
          // Backend registration failed for another reason.
          // Delete the Firebase user so the account is not left in a broken half-registered state.
          console.warn('⚠️ Backend registration failed. Cleaning up Firebase user...');
          try {
            await userCredential.user.delete();
            console.log('✓ Firebase user deleted (cleanup after failed backend registration)');
          } catch (deleteError) {
            console.error('⚠️ Failed to delete Firebase user during cleanup:', deleteError.message);
          }
          // Re-throw so the outer catch shows the user a proper error message
          throw apiError;
        }
      }

      // Step 4b: Create broker profile for broker-type roles
      // ProfilePage's Company section reads from GET /api/v1/users/{userId}/broker-profile —
      // a separate record that POST /api/v1/users does not populate. PUT it now so the
      // Company section is pre-filled on first visit to My Profile.
      if (userId && isBrokerType(formData.role) && (formData.brokerageName || formData.brokerLicenseNumber)) {
        try {
          await apiClient.put(
            API_ENDPOINTS.brokerProfile(userId),
            {
              companyName:          formData.brokerageName        || null,
              companyLicenseNumber: formData.brokerLicenseNumber  || null,
              companyLicenseState:  formData.state                || null,
              companyAddress:       formData.brokerageAddress     || null,
              narId:                formData.brokerageNarId       || null,
            },
            { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' } }
          );
          console.log('✓ Broker profile created');
        } catch (brokerProfileErr) {
          // Non-critical — company fields can be filled in from My Profile
          console.error('Broker profile creation failed, continuing:', brokerProfileErr);
        }
      }

      // Step 5: Upload photo if provided (independent of backend registration status)
      if (formData.photo && userId) {
        console.log('Step 5: Uploading profile photo to backend...');
        setStatus({ type: 'info', message: 'Uploading profile photo...' });

        try {
          const photoFormData = new FormData();
          photoFormData.append('photo', formData.photo);

          console.log('FormData created, uploading photo for user:', userId);

          const photoResponse = await apiClient.post(
            API_ENDPOINTS.userPhoto(userId),
            photoFormData,
            {
              headers: {
                'Authorization': `Bearer ${firebaseToken}`,
                'Content-Type': undefined  // Remove default application/json so browser sets multipart/form-data with boundary
              }
            }
          );

          console.log('✓ Profile photo uploaded successfully:', photoResponse.data);

          // Update backendResponse with photo URL if returned
          if (photoResponse.data) {
            if (!backendResponse) backendResponse = {};
            backendResponse.photoUrl = photoResponse.data.photoUrl || photoResponse.data.url || photoResponse.data.path;
          }

          // Step 5a: Fetch the photo back to get base64 data for immediate display
          try {
            console.log('Fetching uploaded photo for base64 data...');
            const getPhotoResponse = await apiClient.get(
              API_ENDPOINTS.userPhoto(userId),
              {
                headers: {
                  'Authorization': `Bearer ${firebaseToken}`
                },
                responseType: 'blob' // Request as blob for binary image
              }
            );

            console.log('✓ Photo fetched successfully');

            if (getPhotoResponse.data) {
              if (!backendResponse) backendResponse = {};

              // Convert blob to base64
              const blob = getPhotoResponse.data;
              const base64 = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob);
              });

              backendResponse.profilePhotoBase64 = base64;
              console.log('✓ Photo converted to base64 successfully');
            }
          } catch (getPhotoError) {
            console.error('Failed to fetch photo base64, continuing:', getPhotoError);
          }

        } catch (photoError) {
          console.error('Photo upload failed, continuing without photo:', photoError);
          console.error('Photo error details:', {
            message: photoError.message,
            response: photoError.response?.data,
            status: photoError.response?.status
          });
          // Continue without photo - user can add later from profile
        }
      }

      // Step 5b: Upload company logo if provided (Company vendor only — separate
      // from profile photo above, both storage and endpoint).
      if (isCompanyVendor && formData.companyLogoFile && userId) {
        console.log('Step 5b: Uploading company logo to backend...');
        setStatus({ type: 'info', message: 'Uploading company logo...' });

        try {
          const logoFormData = new FormData();
          logoFormData.append('logo', formData.companyLogoFile);

          const logoResponse = await apiClient.post(
            API_ENDPOINTS.companyLogo(userId),
            logoFormData,
            {
              headers: {
                'Authorization': `Bearer ${firebaseToken}`,
                'Content-Type': undefined
              }
            }
          );

          console.log('✓ Company logo uploaded successfully:', logoResponse.data);

          if (logoResponse.data) {
            if (!backendResponse) backendResponse = {};
            backendResponse.companyLogoBase64 = logoResponse.data.companyLogoBase64;
            backendResponse.companyLogoUrl = logoResponse.data.companyLogoUrl;
          }
        } catch (logoError) {
          console.error('Company logo upload failed, continuing without logo:', logoError);
          // Continue without logo - user can add later from profile
        }
      }

      // Step 5c: Self-link to the picked parent company (INDIVIDUAL SPs only).
      // The register endpoint's DTO doesn't carry parentCompanyUserId, so PATCH
      // it onto the sp-profile after register. Non-SP registrations and SPs
      // who didn't pick a company skip this call entirely. Non-fatal on
      // failure — the SP can pick from the profile page later.
      if (isSpType(formData.role) && userId && parentCompanyUserId) {
        try {
          await apiClient.patch(
            API_ENDPOINTS.spProfile,
            { parentCompanyUserId },
            { headers: { 'Authorization': `Bearer ${firebaseToken}` } },
          );
        } catch (companyErr) {
          console.error('[RegisterPage] parent-company PATCH failed, continuing:', companyErr);
        }
      }

      // Step 6: Store authentication state
      setStatus({
        type: 'success',
        message: 'Registration Complete!'
      });

      // Preserve the role the user registered with — backend response must not override it
      const registeredRole = formData.role?.toUpperCase() || null;

      const userData = {
        firstName: formData.firstName,
        middleName: formData.middleName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        state: formData.state,
        city: formData.city,
        firebaseUid: userCredential.user.uid,
        isAuthenticated: true,
        registrationTime: new Date().toISOString(),
        ...(backendResponse || {}), // Include backend data if available
        // These must come AFTER the spread so they are never overwritten by backend response
        role: registeredRole,
        profilePhotoBase64: backendResponse?.profilePhotoBase64 || null,
        profilePhotoUrl: backendResponse?.profilePhotoUrl || backendResponse?.photoUrl || null,
        // Cache company fields under the names ProfilePage expects (broker-profile field mapping)
        // so the My Profile Company section is pre-filled even on the very first load
        ...(isBrokerType(formData.role) ? {
          companyName:          formData.brokerageName       || null,
          companyLicenseNumber: formData.brokerLicenseNumber || null,
          companyLicenseState:  formData.state               || null,
          companyAddress:       formData.brokerageAddress    || null,
          companyNarId:         formData.brokerageNarId      || null,
        } : {}),
        // Company vendor: no first/last name was ever set, so any generic "Hi,
        // {firstName}" surface elsewhere in the app falls back to the business
        // name instead of showing blank/null.
        ...(isCompanyVendor ? {
          firstName:          backendResponse?.businessName || formData.spBusinessName || null,
          lastName:           null,
          businessName:       backendResponse?.businessName || formData.spBusinessName || null,
          vendorType:         'COMPANY',
          companyLogoBase64:  backendResponse?.companyLogoBase64 || null,
          companyLogoUrl:     backendResponse?.companyLogoUrl    || null,
        } : {}),
      };

      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('firebaseToken', firebaseToken);
      // Pin the active role explicitly so DashboardHeader / ProfilePage read
      // the correct one immediately. Priority:
      //   1. registeredRole — what the user picked in the register form
      //   2. backend's authoritative roles[0] — covers direct /register
      //      landings (no role in formData) where the backend defaults to
      //      OTHER. Without this fallback a stale currentRole from a prior
      //      session on the same browser would carry over and lie about
      //      the freshly-registered user's role.
      const backendPrimaryRole = Array.isArray(backendResponse?.roles) && backendResponse.roles.length > 0
        ? String(backendResponse.roles[0]).toUpperCase()
        : null;
      const roleToPin = registeredRole || backendPrimaryRole;
      if (roleToPin) {
        localStorage.setItem('currentRole', roleToPin);
      } else {
        // No role at all → make sure a stale value doesn't lie about the new user.
        localStorage.removeItem('currentRole');
      }

      // Clear stored verification data after successful registration
      sessionStorage.removeItem('agentVerificationData');

      // Accept any pending invite (type=user or type=transaction) — mirrors mobile RegistrationProgressScreen
      const inviteHandled = await handlePendingInvite(firebaseToken, userData);
      if (inviteHandled) return;

      sessionStorage.removeItem('inviteData');

      // Navigate to MFA enrollment after success (mirrors mobile post-registration flow)
      setTimeout(() => {
        navigate('/mfa-enrollment');
      }, 1500);

    } catch (error) {
      console.error('Registration error:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        name: error.name,
        stack: error.stack
      });

      // Handle Firebase errors
      if (error.code && error.code.startsWith('auth/')) {
        switch (error.code) {
          case 'auth/email-already-in-use':
            setStatus({
              type: 'error',
              message: 'This email is already registered. Please login instead.'
            });
            break;
          case 'auth/invalid-email':
            setStatus({
              type: 'error',
              message: 'Invalid email address format.'
            });
            break;
          case 'auth/weak-password':
            setStatus({
              type: 'error',
              message: 'Password is too weak. Please use a stronger password.'
            });
            break;
          case 'auth/network-request-failed':
            setStatus({
              type: 'error',
              message: 'Network error. Please check your internet connection.'
            });
            break;
          case 'auth/too-many-requests':
            setStatus({
              type: 'error',
              message: 'Too many attempts. Please try again later.'
            });
            break;
          default:
            setStatus({
              type: 'error',
              message: error.message || 'Registration failed. Please try again.'
            });
        }
      } else if (error.response) {
        // Backend API error with response
        console.error('API Response Error:', error.response.data);
        setStatus({
          type: 'error',
          message: error.response.data?.message || 'Server error. Please try again.'
        });
      } else if (error.request) {
        // Network error - request was made but no response
        console.error('Network Error - No response received:', error.request);
        setStatus({
          type: 'error',
          message: 'Cannot connect to server. Please check your internet connection.'
        });
      } else {
        // Other errors
        setStatus({
          type: 'error',
          message: error.message || 'Registration failed. Please try again.'
        });
      }

      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      // Step 1: Sign in with Google using Firebase
      setStatus({ type: 'info', message: 'Connecting to Google...' });
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);

      // Step 2: Get Firebase auth token and user info
      const firebaseToken = await result.user.getIdToken();
      const firebaseUid = result.user.uid;
      const userEmail = result.user.email;
      const userName = result.user.displayName;

      console.log('Google authentication successful');
      console.log('User email:', userEmail);
      console.log('User name:', userName);

      // Step 3: Register user in backend
      setStatus({ type: 'info', message: 'Creating your account...' });

      // Parse name from Google account
      const nameParts = (userName || userEmail.split('@')[0]).split(' ');
      const googleFirstName = nameParts[0] || '';
      const googleLastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
      const googleMiddleName = nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : '';

      // Company vendor identity must hold regardless of signup method — a Company
      // invite can't be bypassed into person-name fields just by using Google Sign-In.
      const isCompanyVendor = isCompanyVendorType(formData.role, formData.spVendorType);
      const registrationData = {
        ...(isCompanyVendor ? {} : {
          firstName: googleFirstName,
          middleName: googleMiddleName || null,
          lastName: googleLastName,
        }),
        email: userEmail,
        firebaseUid: firebaseUid,
        role: formData.role?.toUpperCase() || null,
        phone: formData.phone ? toE164US(formData.phone) : null,
        // Address fields
        street: formData.street || null,
        state: formData.state || null,
        city: formData.city || null,
        postalCode: formData.zipCode || null,
        // License/agent fields
        // Preserve the state the license was verified in — falls back to the
        // editable mailing-address state when no verification took place
        // (mirrors mobile licenseStateFromVerification || getStateCode(state))
        licenseState: formData.licenseStateFromVerification || formData.state || null,
        licenseCity: formData.licenseCityFromVerification || null,
        licenseZip: formData.licenseZipFromVerification || null,
        agentNumber: formData.licenseNumber || null,
        narId: formData.narId || null,
        // Brokerage fields
        brokerageName: formData.brokerageName || null,
        brokerOfRecordName: formData.brokerOfRecordName || null,
        // AGENT: firm's licence goes to brokerLicenseNumber (mirrors mobile — agent
        // payload has no companyLicenseNumber key at all).
        // BROKER: the broker's own verified personal DRE licence, captured silently
        // from BrokerVerificationPage — never the editable "Company License Number"
        // field (mirrors mobile brokerLicenseFromVerification → brokerLicenseNumber).
        ...(formData.role?.toLowerCase() === 'agent'
          ? { brokerLicenseNumber: formData.brokerLicenseNumber || null }
          : isBrokerType(formData.role)
            ? { brokerLicenseNumber: formData.brokerVerifiedLicenseNumber || null }
            : {}),
        // companyLicenseNumber: firm/brokerage DRE licence — BROKER only, sourced from
        // the editable "Company License Number" field (mirrors mobile brokerageLicenseNumber
        // → companyLicenseNumber; the agent registration payload never sends this key).
        companyLicenseNumber: isBrokerType(formData.role)
          ? (formData.brokerLicenseNumber || null)
          : null,
        brokerageAddress: formData.brokerageAddress || null,
        brokerageNarId: formData.brokerageNarId || null,
        // Firm's license type (e.g. "Corporation") — mobile sends this under
        // brokerageLicenseType for both agent and broker registration.
        brokerageLicenseType: formData.licenseType || null,
        brokerageLicenseId: formData.brokerageLicenceId || null,
        // SP / Vendor fields (only included for those roles)
        ...(isSpType(formData.role) ? {
          serviceTypes:      formData.spServiceTypes?.length > 0 ? formData.spServiceTypes : null,
          serviceArea:       formData.spServiceArea                                                   || null,
          yearsOfExperience: formData.spYearsOfExperience ? parseInt(formData.spYearsOfExperience, 10) : null,
          licenseNumber:     formData.spLicenseNumber                                                 || null,
          licenseState:      formData.spLicenseState || formData.state                                || null,
          vendorType:        formData.spVendorType                                                    || null,
          businessName:      isCompanyVendor ? (formData.spBusinessName || null) : null,
        } : {}),
      };

      console.log('Registering user with backend:', registrationData);

      let backendResponse = null;
      try {
        const response = await apiClient.post(
          API_ENDPOINTS.register,
          registrationData,
          {
            headers: {
              'Authorization': `Bearer ${firebaseToken}`,
              'Content-Type': 'application/json'
            },
            timeout: 10000
          }
        );

        console.log('Backend registration successful:', response.data);
        backendResponse = response.data;

      } catch (apiError) {
        console.error('Backend API Error:', apiError);
        console.error('Error details:', {
          message: apiError.message,
          code: apiError.code,
          response: apiError.response?.data,
          status: apiError.response?.status
        });

        // Continue with registration even if backend fails
        console.warn('Continuing with registration despite backend error');
      }

      // Self-link to picked parent company (INDIVIDUAL SPs only), same as the
      // email/password path (Step 5c there). Skipped for non-SP roles and when
      // no company was picked.
      const googleUserId = backendResponse?.id || backendResponse?.data?.id || firebaseUid;
      if (isSpType(formData.role) && googleUserId && parentCompanyUserId) {
        try {
          await apiClient.patch(
            API_ENDPOINTS.spProfile,
            { parentCompanyUserId },
            { headers: { 'Authorization': `Bearer ${firebaseToken}` } },
          );
        } catch (companyErr) {
          console.error('[RegisterPage] parent-company PATCH failed (google path), continuing:', companyErr);
        }
      }

      // Step 4: Store authentication state
      setStatus({
        type: 'success',
        message: 'Registration Complete!'
      });

      // Preserve the role the user registered with — backend response must not override it
      const registeredRoleGoogle = formData.role?.toUpperCase() || null;

      const userData = {
        firstName: googleFirstName,
        middleName: googleMiddleName,
        lastName: googleLastName,
        email: userEmail,
        firebaseUid: firebaseUid,
        isAuthenticated: true,
        registrationTime: new Date().toISOString(),
        loginMethod: 'google',
        ...(backendResponse || {}),
        // Must come AFTER spread so backend response cannot override the registered role
        role: registeredRoleGoogle,
        // Company vendor: same fallback as the email/password path — show the
        // business name wherever a generic "Hi, {firstName}" surface reads it.
        ...(isCompanyVendor ? {
          firstName:    backendResponse?.businessName || formData.spBusinessName || null,
          lastName:     null,
          businessName: backendResponse?.businessName || formData.spBusinessName || null,
          vendorType:   'COMPANY',
        } : {}),
      };

      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('firebaseToken', firebaseToken);
      // Explicitly pin the active role so DashboardHeader reads the correct one immediately
      if (registeredRoleGoogle) localStorage.setItem('currentRole', registeredRoleGoogle);

      // Clear stored verification data
      sessionStorage.removeItem('agentVerificationData');

      // Accept any pending invite (type=user or type=transaction) — mirrors mobile RegistrationProgressScreen
      const inviteHandledGoogle = await handlePendingInvite(firebaseToken, userData);
      if (inviteHandledGoogle) return;

      sessionStorage.removeItem('inviteData');

      // Navigate to MFA enrollment after success (mirrors mobile post-registration flow)
      setTimeout(() => {
        navigate('/mfa-enrollment');
      }, 1500);

    } catch (error) {
      console.error('Google Sign-In error:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        name: error.name
      });

      // Handle Firebase Google Sign-In errors
      if (error.code) {
        switch (error.code) {
          case 'auth/operation-not-allowed':
            setStatus({
              type: 'error',
              message: 'Google Sign-In is not enabled. Please contact administrator.'
            });
            console.error('FIREBASE SETUP REQUIRED: Enable Google Sign-In in Firebase Console > Authentication > Sign-in method');
            break;
          case 'auth/popup-closed-by-user':
            setStatus({
              type: 'error',
              message: 'Sign-in popup was closed. Please try again.'
            });
            break;
          case 'auth/cancelled-popup-request':
            setStatus({
              type: 'error',
              message: 'Sign-in was cancelled. Please try again.'
            });
            break;
          case 'auth/popup-blocked':
            setStatus({
              type: 'error',
              message: 'Pop-up was blocked by browser. Please allow pop-ups for this site.'
            });
            break;
          case 'auth/account-exists-with-different-credential':
            setStatus({
              type: 'error',
              message: 'An account already exists with this email using a different sign-in method.'
            });
            break;
          case 'auth/email-already-in-use':
            setStatus({
              type: 'error',
              message: 'This email is already registered. Please login instead.'
            });
            break;
          case 'auth/network-request-failed':
            setStatus({
              type: 'error',
              message: 'Network error. Please check your internet connection.'
            });
            break;
          default:
            setStatus({
              type: 'error',
              message: error.message || 'Google sign-in failed. Please try again.'
            });
        }
      } else {
        setStatus({
          type: 'error',
          message: 'Google sign-in failed. Please try again.'
        });
      }

      setIsSubmitting(false);
    }
  };


  return (
    <div className="register-page">
      <div className="registration-container">
        <div className="header-section">
          <h1 className="title">Just a few more details.</h1>
          <p className="subtitle">Complete your profile to get started</p>
        </div>

        {/* Invited-by banner — only when the inviter is a COMPANY-vendor SP
            and the backend returned a company name on the invite validation
            response. Pure display; the invitee is not auto-attached to the
            company (parent-company linkage stays an explicit opt-in). */}
        {formData.inviterCompanyName && (
          <div className="invited-by-banner">
            {formData.inviterCompanyLogoUrl && (
              <img
                src={
                  formData.inviterCompanyLogoUrl.startsWith('http')
                    ? formData.inviterCompanyLogoUrl
                    : `${API_BASE_URL}${formData.inviterCompanyLogoUrl}`
                }
                alt=""
                className="invited-by-logo"
              />
            )}
            <div className="invited-by-text">
              <span className="invited-by-label">Invited by</span>
              <span className="invited-by-company">{formData.inviterCompanyName}</span>
            </div>
          </div>
        )}

        {formData.role === 'agent' && (
          <div className="agent-badge">
            <span className="agent-id">Agent License: {formData.licenseNumber}</span>
            <div className="verified-badge">
              <span>Verified</span>
              <div className="verified-icon">✓</div>
            </div>
          </div>
        )}

        {isBrokerType(formData.role) && formData.brokerLicenseNumber && (
          <div className="agent-badge">
            <span className="agent-id">License: {formData.brokerLicenseNumber}</span>
            <div className="verified-badge">
              <span>Verified</span>
              <div className="verified-icon">✓</div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {isCompanyVendorType(formData.role, formData.spVendorType) ? (
            <>
              <div className="form-group">
                <div className="input-wrapper">
                  <input
                    type="text"
                    name="spBusinessName"
                    className="form-input"
                    placeholder="Business name"
                    value={formData.spBusinessName}
                    onChange={handleChange}
                    required
                  />
                  <span className="input-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 21h18" /><path d="M5 21V7l8-4v18" /><path d="M19 21V11l-6-4" />
                    </svg>
                  </span>
                </div>
              </div>

              {companyLogoPreview && (
                <div className="upload-preview">
                  <img src={companyLogoPreview} alt="Company logo preview" />
                </div>
              )}
              <label className="photo-upload">
                <div className="upload-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </div>
                <span className="upload-text">
                  {formData.companyLogoFile?.name || 'Add company logo (optional)'}
                </span>
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setFormData({ ...formData, companyLogoFile: file });
                    if (companyLogoPreview && companyLogoPreview.startsWith('blob:')) {
                      URL.revokeObjectURL(companyLogoPreview);
                    }
                    setCompanyLogoPreview(file ? URL.createObjectURL(file) : '');
                  }}
                  style={{ display: 'none' }}
                />
              </label>
            </>
          ) : (
            <>
              <div className="form-row">
                <div className="input-wrapper">
                  <input
                    type="text"
                    name="firstName"
                    className="form-input"
                    placeholder="First name"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                  <span className="input-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </span>
                </div>

                <div className="input-wrapper">
                  <input
                    type="text"
                    name="middleName"
                    className="form-input"
                    placeholder="Middle name (optional)"
                    value={formData.middleName}
                    onChange={handleChange}
                  />
                  <span className="input-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </span>
                </div>
              </div>

              <div className="form-group">
                <div className="input-wrapper">
                  <input
                    type="text"
                    name="lastName"
                    className="form-input"
                    placeholder="Last name"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                  <span className="input-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </span>
                </div>
              </div>
            </>
          )}

          <div className="form-group">
            <div className="input-wrapper">
              <input
                type="email"
                name="email"
                className={`form-input ${isEmailPreFilled ? 'input-readonly' : ''}`}
                placeholder="Email address"
                value={formData.email}
                onChange={handleChange}
                required
                readOnly={isEmailPreFilled}
              />
              <span className="input-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </span>
            </div>
          </div>

          <div className="form-group">
            <div className="input-wrapper">
              <input
                type="tel"
                name="phone"
                className={`form-input ${phoneError ? 'input-error' : ''}`}
                placeholder="Mobile Number"
                value={formData.phone}
                onChange={handleChange}
                required
              />
              <span className="input-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
              </span>
            </div>
            {phoneError && (
              <span className="phone-error">{phoneError}</span>
            )}
          </div>

          <div className="form-group">
            <div className="input-wrapper password-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                className="form-input"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
                required
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex="-1"
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                )}
              </button>
            </div>
            {formData.password.length > 0 && (
              <div className="reg-requirements">
                {passwordRules.map(rule => (
                  <div key={rule.id} className={`reg-req-item ${rule.test(formData.password) ? 'met' : ''}`}>
                    <span className="reg-req-icon">
                      {rule.test(formData.password) ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <circle cx="12" cy="12" r="10" />
                        </svg>
                      )}
                    </span>
                    <span>{rule.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-group">
            <div className="input-wrapper password-wrapper">
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                className="form-input"
                placeholder="Confirm Password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                tabIndex="-1"
              >
                {showConfirmPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* SP License Number — placed directly above Licensed Address so the
              two license-related fields sit next to each other, instead of
              License Number being separated from Licensed Address by the
              Personal Address and Service Type(s) sections further down.
              Still SP-only and still unconditional on licenseStateFromVerification
              (same as before this field was relocated) — only its position moved. */}
          {isSpType(formData.role) && (
            <div className="form-group">
              <div className="input-wrapper">
                <input
                  type="text"
                  name="spLicenseNumber"
                  className="form-input"
                  placeholder="License Number (Optional)"
                  value={formData.spLicenseNumber}
                  onChange={handleChange}
                />
                <span className="input-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </span>
              </div>
            </div>
          )}

          {/* Licensed Address — the state/city/zip this license is actually
              registered under, kept distinct from the Personal Address below
              (which can legitimately be in a different state — a Colorado
              license doesn't require a Colorado mailing address). Only shown
              once the user has come through Agent/Broker license
              verification. No Street field: the state registry (e.g. CO
              DORA) has no street-level data for a license — it's issued to
              a state/city, not an address. All three fields are optional for
              SP — an SP's "license" here is just a self-reported service
              area, not a verified state registration. State is readOnly/
              unrequired for everyone; City is a CitySelect dropdown with no
              native required enforcement for anyone either way; only Zip
              carries an explicit required (Agent/Broker) vs optional (SP)
              distinction below. Hidden for Buyer/Seller — they hold no license,
              but an invite still seeds licenseStateFromVerification from the
              inviter's/property's state (mirrors mobile RegisterScreen, which
              has no Licensed Address section). */}
          {!!formData.licenseStateFromVerification &&
            !['buyer', 'seller'].includes((formData.role || '').toLowerCase()) && (
            <>
              <h3 className="brokerage-card-title">Licensed Address</h3>
              <div className="form-row">
                <div className="input-wrapper">
                  <input
                    type="text"
                    className="form-input input-readonly"
                    placeholder="Licensed State"
                    value={
                      stateCityData.find((item) => item.stateCode === formData.licenseStateFromVerification)?.state
                      || formData.licenseStateFromVerification
                    }
                    readOnly
                  />
                </div>
                <div className="input-wrapper">
                  <CitySelect
                    value={formData.licenseCityFromVerification}
                    onChange={(city) => handleChange({ target: { name: 'licenseCityFromVerification', value: city } })}
                    stateValue={formData.licenseStateFromVerification}
                    stateMatchField="stateCode"
                  />
                </div>
              </div>
              <div className="form-group">
                <div className="input-wrapper">
                  <input
                    type="text"
                    name="licenseZipFromVerification"
                    className="form-input"
                    placeholder="Licensed Zip Code"
                    value={formData.licenseZipFromVerification}
                    onChange={handleChange}
                    maxLength="8"
                    required={!isSpType(formData.role)}
                  />
                </div>
              </div>
            </>
          )}

          {/* Personal Address — the licensee's own mailing/contact address,
              reported exactly as the registry/ARELLO gives it regardless of
              state (see Licensed Address above for the license's own
              state/city/zip). */}
          <h3 className="brokerage-card-title">Personal Address</h3>
          <div className="form-group">
            <div className="input-wrapper">
              {/* Google autocomplete for every role (same as ProfilePage's
                  Personal Address Street). No restrictToStateCode — a personal
                  address may be in any state. A verified agent whose street is
                  locked keeps the plain read-only input below. */}
              {!(formData.role === 'agent' && formData.agentStreetLocked) ? (
                <AddressAutocomplete
                  value={formData.street}
                  onChange={(text) => setFormData(prev => ({ ...prev, street: text }))}
                  onAddressSelect={(parsed) => {
                    // Never overwrite fields a verified agent has locked.
                    const isAgentRole = formData.role === 'agent';
                    const fillState = !(isAgentRole && formData.agentStateLocked);
                    const fillCity = !(isAgentRole && formData.agentCityLocked);
                    const fillZip = !(isAgentRole && formData.agentZipLocked);
                    setFormData(prev => ({
                      ...prev,
                      street: parsed.street || prev.street,
                      ...(parsed.stateCode && {
                        ...(fillState && { state: parsed.stateCode }),
                        ...(fillCity && { city: parsed.city || '' }),
                        ...(fillZip && { zipCode: parsed.zip || '' }),
                        ...(isSpType(prev.role) && {
                          spLicenseState: prev.spLicenseState || parsed.stateCode,
                        }),
                      }),
                    }));
                    if (parsed.stateCode && fillZip) {
                      const zipValidation = parsed.zip ? validateZipCode(parsed.zip) : { isValid: true };
                      setZipCodeError(zipValidation.isValid ? '' : zipValidation.message);
                    }
                  }}
                  placeholder="Street address"
                  inputClassName="form-input"
                />
              ) : (
                <>
                  <input
                    type="text"
                    name="street"
                    className="form-input"
                    placeholder="Street address"
                    value={formData.street}
                    onChange={handleChange}
                    required
                    disabled={formData.role === 'agent' && formData.agentStreetLocked}
                  />
                  <span className="input-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  </span>
                </>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="input-wrapper">
              <select
                name="state"
                className="form-input form-select"
                value={formData.state}
                onChange={handleChange}
                required
                disabled={formData.role === 'agent' && formData.agentStateLocked}
              >
                <option value="">State</option>
                {stateCityData.map((item) => (
                  <option key={item.state} value={item.stateCode}>
                    {item.state}
                  </option>
                ))}
              </select>
              <span className="dropdown-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </div>

            <div className="input-wrapper">
              <CitySelect
                value={formData.city}
                onChange={(city) => handleChange({ target: { name: 'city', value: city } })}
                stateValue={formData.state}
                stateMatchField="stateCode"
                disabled={formData.role === 'agent' && formData.agentCityLocked}
              />
            </div>
          </div>

          <div className="form-group">
            <div className="input-wrapper">
              <input
                type="text"
                name="zipCode"
                className={`form-input ${zipCodeError ? 'input-error' : ''}`}
                placeholder="Zip code"
                value={formData.zipCode}
                onChange={handleChange}
                required
                maxLength="8"
                disabled={formData.role === 'agent' && formData.agentZipLocked}
              />
            </div>
            {zipCodeError && (
              <span className="zip-error">{zipCodeError}</span>
            )}
          </div>

          {/* Broker/Brokerage section — shown for AGENT and all broker-type roles (mirrors mobile isBrokerType) */}
          {(formData.role === 'agent' || isBrokerType(formData.role)) && (
            <div className="brokerage-card">
              <h3 className="brokerage-card-title">
                {isBrokerType(formData.role)
                  ? (formData.brokerageLockedFromInvite
                      ? (formData.isDesignatedBrokerInvite ? 'Brokerage you are activating' : 'Joining brokerage')
                      : 'Brokerage (optional)')
                  : 'Brokerage Information (Optional)'}
              </h3>

              {isBrokerType(formData.role) && (
                <p className="brokerage-helper-text">
                  {formData.brokerageLockedFromInvite
                    ? 'These details are locked from the invite and cannot be changed here.'
                    : "Enter your brokerage's licence number to be linked to that firm. Leave blank to register as a standalone broker."}
                </p>
              )}

              <div className="brokerage-field">
                <input
                  type="text"
                  name="brokerLicenseNumber"
                  className="brokerage-input"
                  placeholder={isBrokerType(formData.role) ? 'Company License Number (Optional)' : 'Broker License Number'}
                  value={formData.brokerLicenseNumber}
                  onChange={handleChange}
                  onBlur={handleBrokerLicenseBlur}
                  readOnly={isBrokerType(formData.role) && formData.brokerageLockedFromInvite}
                />
                <span className="brokerage-field-icon">
                  {isBrokerVerifying ? (
                    <span className="btn-spinner" style={{ width: 16, height: 16, borderWidth: 2 }}></span>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  )}
                </span>
              </div>

              <div className="brokerage-field">
                <input
                  type="text"
                  name="brokerageName"
                  className="brokerage-input"
                  placeholder={isBrokerType(formData.role) ? 'Company Name (Optional)' : 'Brokerage Name'}
                  value={formData.brokerageName}
                  onChange={handleChange}
                  readOnly={isBrokerType(formData.role) && formData.brokerageLockedFromInvite}
                />
                <span className="brokerage-field-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    <polyline points="9 22 9 12 15 12 15 22" />
                  </svg>
                </span>
              </div>

              <div className="brokerage-field">
                <input
                  type="text"
                  name="licenseType"
                  className="brokerage-input"
                  placeholder="License Type"
                  value={formData.licenseType}
                  onChange={handleChange}
                />
                <span className="brokerage-field-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </span>
              </div>

              <div className="brokerage-field">
                <input
                  type="text"
                  name="brokerageAddress"
                  className="brokerage-input"
                  placeholder={isBrokerType(formData.role) ? 'Company Address (Optional)' : 'Brokerage Address'}
                  value={formData.brokerageAddress}
                  onChange={handleChange}
                />
                <span className="brokerage-field-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </span>
              </div>

              <div className="brokerage-field">
                <input
                  type="text"
                  name="brokerageNarId"
                  className="brokerage-input"
                  placeholder={isBrokerType(formData.role) ? 'Company NAR ID (Optional)' : 'Brokerage NAR ID'}
                  value={formData.brokerageNarId}
                  onChange={handleChange}
                />
                <span className="brokerage-field-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="16" rx="2" />
                    <line x1="7" y1="8" x2="17" y2="8" />
                    <line x1="7" y1="12" x2="13" y2="12" />
                  </svg>
                </span>
              </div>

              {isBrokerType(formData.role) && (
                <>
                  <div className="brokerage-field">
                    <input
                      type="text"
                      name="brokerageLicenceId"
                      className="brokerage-input"
                      placeholder="Brokerage Licence ID (Optional)"
                      value={formData.brokerageLicenceId}
                      onChange={handleChange}
                    />
                    <span className="brokerage-field-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                    </span>
                  </div>
                  <p className="brokerage-helper-text">
                    Enter the Brokerage Licence ID to affiliate with an existing brokerage on Rialty.
                  </p>
                </>
              )}
            </div>
          )}

          {/* ── SP / Vendor fields ── */}
          {isSpType(formData.role) && (
            <>
              <div className="form-group">
                <div className="sp-service-label">
                  Service Type(s) <span style={{ color: '#FF3B30' }}>*</span>
                  {formData.spServiceTypes.length > 0 && (
                    <span className="sp-service-count">{formData.spServiceTypes.length} selected</span>
                  )}
                </div>
                <div className="sp-service-pill-grid">
                  {serviceTypeOptions.map(opt => {
                    const selected = formData.spServiceTypes.includes(opt.name);
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        className={`sp-service-pill${selected ? ' sp-service-pill-selected' : ''}`}
                        onClick={() => toggleSpServiceType(opt.name)}
                      >
                        {selected && (
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                        {opt.name}
                      </button>
                    );
                  })}
                </div>
                <span className="sp-service-hint">Select one or more service types</span>
              </div>

              <div className="form-group">
                <div className="input-wrapper">
                  <input
                    type="text"
                    name="spServiceArea"
                    className="form-input"
                    placeholder="Service Area (e.g. San Francisco Bay Area)"
                    value={formData.spServiceArea}
                    onChange={handleChange}
                    required
                  />
                  <span className="input-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="2" y1="12" x2="22" y2="12"/>
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                    </svg>
                  </span>
                </div>
              </div>

              <div className="form-row">
                <div className="input-wrapper">
                  <input
                    type="text"
                    name="spYearsOfExperience"
                    className="form-input"
                    placeholder="Years of Experience"
                    value={formData.spYearsOfExperience}
                    onChange={handleChange}
                    inputMode="numeric"
                    required
                  />
                  <span className="input-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                    </svg>
                  </span>
                </div>

                <div className="input-wrapper">
                  <select
                    name="spLicenseState"
                    className="form-input form-select"
                    value={formData.spLicenseState}
                    onChange={handleChange}
                    required
                  >
                    <option value="">License State</option>
                    {stateCityData.map((item) => (
                      <option key={item.state} value={item.stateCode}>{item.state}</option>
                    ))}
                  </select>
                  <span className="dropdown-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </div>
              </div>

              {/* Company Information (optional, INDIVIDUAL vendors only).
                  Picker searches existing Rialty COMPANY-vendor SPs by
                  businessName / email / phone; on pick we show the selected
                  company's card and PATCH parentCompanyUserId after register.
                  Hidden in two cases:
                  - COMPANY-vendor registration (they ARE the company).
                  - Invite from a COMPANY SP (inviter.inviterCompanyName set):
                    the invitee is already auto-linked to that company via
                    InvitationService on accept — asking them to also pick
                    one would be confusing and could stomp the auto-link. */}
              {!isCompanyVendorType(formData.role, formData.spVendorType) &&
                !formData.inviterCompanyName && (
                <>
                  <h3 className="brokerage-card-title">Company Information (Optional)</h3>
                  {parentCompany ? (
                    <div className="sp-parent-company-card">
                      {parentCompany.companyLogoUrl &&
                        (parentCompany.companyLogoUrl.startsWith('http://') ||
                         parentCompany.companyLogoUrl.startsWith('https://') ||
                         parentCompany.companyLogoUrl.startsWith('data:')) ? (
                        <img
                          src={parentCompany.companyLogoUrl}
                          alt={parentCompany.businessName || 'Company'}
                          className="sp-parent-company-logo"
                        />
                      ) : (
                        <div className="sp-parent-company-logo sp-parent-company-logo-fallback">
                          {(parentCompany.businessName || 'C').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="sp-parent-company-body">
                        <p className="sp-parent-company-name">
                          {parentCompany.businessName || 'Company'}
                        </p>
                        {(parentCompany.city || parentCompany.state) && (
                          <p className="sp-parent-company-meta">
                            {[parentCompany.city, parentCompany.state].filter(Boolean).join(', ')}
                          </p>
                        )}
                        {parentCompany.email && (
                          <p className="sp-parent-company-meta">{parentCompany.email}</p>
                        )}
                        {parentCompany.phone && (
                          <p className="sp-parent-company-meta">{parentCompany.phone}</p>
                        )}
                      </div>
                    </div>
                  ) : null}
                  <div className="form-group" style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      className="btn-cancel"
                      onClick={() => {
                        setCompanySearchQuery('');
                        setCompanySearchResults([]);
                        setShowCompanyPickerModal(true);
                      }}
                    >
                      {parentCompanyUserId ? 'Change' : 'Find my company'}
                    </button>
                    {parentCompanyUserId && (
                      <button
                        type="button"
                        className="btn-cancel"
                        onClick={handleCompanyUnlink}
                      >
                        Unlink
                      </button>
                    )}
                  </div>
                </>
              )}

            </>
          )}

          {formData.photo && (
            <div className="upload-preview upload-preview--round">
              <img src={formData.photo} alt="Profile photo preview" />
            </div>
          )}
          <label className="photo-upload">
            <div className="upload-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </div>
            <span className="upload-text">
              {photoFileName || 'Add photo (optional)'}
            </span>
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              style={{ display: 'none' }}
            />
          </label>

          {/* Terms & Privacy Agreement */}
          <div className="reg-terms-row">
            <input
              type="checkbox"
              id="agreeTerms"
              className="reg-terms-checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
            />
            <label htmlFor="agreeTerms" className="reg-terms-label">
              Yes, I understand and agree to the{' '}
              <button type="button" className="reg-terms-link" onClick={() => setLegalModal('terms')}>
                Rialty Terms of Service
              </button>
              {' '}and{' '}
              <button type="button" className="reg-terms-link" onClick={() => setLegalModal('privacy')}>
                Privacy Policy
              </button>.
            </label>
          </div>

          {/* SMS Consent — separate, unchecked, optional. Not gated into the
              submit button's disabled state the way agreedToTerms is. */}
          <div className="reg-terms-row">
            <input
              type="checkbox"
              id="smsConsent"
              className="reg-terms-checkbox"
              checked={smsConsent}
              onChange={(e) => setSmsConsent(e.target.checked)}
            />
            <label htmlFor="smsConsent" className="reg-terms-label">
              Get transaction updates and verification codes from Rialty by text — invitations to
              join a transaction workspace and one-time codes to verify your identity before
              viewing or signing documents. Msg frequency varies. Msg & data rates may apply.
              Reply STOP to opt out, HELP for help.{' '}
              <a href="/sms-consent" target="_blank" rel="noopener noreferrer" className="reg-terms-link">
                Learn more
              </a>.
            </label>
          </div>

          {status.message && (
            <div className={`status-message ${status.type}`}>
              {status.message}
            </div>
          )}

          <button
            type="submit"
            className="register-btn"
            disabled={isSubmitting || !agreedToTerms}
          >
            {isSubmitting ? 'Creating your account...' : 'Register'}
          </button>
           {/*
          <div className="divider">
            <span className="divider-text">Or</span>
          </div>
           {/*
          <button
            type="button"
            className="social-btn"
            onClick={handleGoogleSignIn}
          >
            <svg className="google-icon" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>*/}

          <div className="auth-footer">
            <p>Already have an account? <Link to="/login" className="link">Login</Link></p>
          </div>
        </form>
      </div>

      {/* Company picker modal — mirrors SpProfilePage.js's picker. Debounced
          search above fires only while this modal is open. */}
      {showCompanyPickerModal && (
        <div
          className="sp-company-picker-overlay"
          onClick={() => setShowCompanyPickerModal(false)}
        >
          <div
            className="sp-company-picker-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sp-company-picker-header">
              <h3>Find your company</h3>
              <button
                type="button"
                className="sp-company-picker-close"
                onClick={() => setShowCompanyPickerModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <input
              type="text"
              className="profile-input"
              value={companySearchQuery}
              onChange={(e) => setCompanySearchQuery(e.target.value)}
              placeholder="Search by company name, email, or phone"
              autoFocus
            />
            <div className="sp-company-picker-results">
              {companySearchQuery.trim().length < 2 ? (
                <p className="sp-company-picker-empty">
                  Start typing a company name, email, or phone to search.
                </p>
              ) : companySearching ? (
                <p className="sp-company-picker-empty">Searching…</p>
              ) : companySearchResults.length === 0 ? (
                <p className="sp-company-picker-empty">No matching companies found.</p>
              ) : (
                companySearchResults.map((row) => (
                  <button
                    key={row.userId}
                    type="button"
                    className="sp-company-picker-row"
                    onClick={() => handleCompanyPick(row)}
                  >
                    {row.companyLogoUrl &&
                      (row.companyLogoUrl.startsWith('http://') ||
                       row.companyLogoUrl.startsWith('https://') ||
                       row.companyLogoUrl.startsWith('data:')) ? (
                      <img
                        src={row.companyLogoUrl}
                        alt={row.businessName || 'Company'}
                        className="sp-company-picker-logo"
                      />
                    ) : (
                      <div className="sp-company-picker-logo sp-parent-company-logo-fallback">
                        {(row.businessName || 'C').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="sp-company-picker-rowbody">
                      <p className="sp-parent-company-name">
                        {row.businessName || 'Company'}
                      </p>
                      {(row.city || row.state) && (
                        <p className="sp-parent-company-meta">
                          {[row.city, row.state].filter(Boolean).join(', ')}
                        </p>
                      )}
                      {row.email && (
                        <p className="sp-parent-company-meta">{row.email}</p>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Legal Content Modal (Terms of Service / Privacy Policy) */}
      {legalModal && (
        <div className="reg-legal-overlay" onClick={() => setLegalModal(null)}>
          <div className="reg-legal-modal" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="reg-legal-header">
              <h2 className="reg-legal-title">
                {legalModal === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
              </h2>
              <button type="button" className="reg-legal-close" onClick={() => setLegalModal(null)} aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {/* Scrollable body */}
            <div className="reg-legal-body">
              {legalModal === 'terms' ? (
                <>
                  <p className="reg-legal-date">Last Updated: November 25, 2025</p>
                  <h3>1. Acceptance of Terms</h3>
                  <p>By accessing and using Rialty.ai ("the Platform"), you accept and agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the Platform.</p>
                  <h3>2. Description of Service</h3>
                  <p>Rialty.ai is an intelligent transaction management platform designed to streamline real estate workflows. The Platform provides tools for document management, collaboration, notifications, and AI-driven automation to support real estate professionals, buyers, and sellers.</p>
                  <h3>3. User Accounts and Registration</h3>
                  <p><strong>3.1 Account Creation:</strong> To use certain features of the Platform, you must create an account. You agree to provide accurate, current, and complete information during registration and to update such information as necessary.</p>
                  <p><strong>3.2 Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must immediately notify us of any unauthorized use of your account or any security breach.</p>
                  <p><strong>3.3 Account Eligibility:</strong> You must be at least 18 years old and have the legal capacity to enter into binding contracts to use the Platform.</p>
                  <h3>4. User Conduct and Prohibited Activities</h3>
                  <p>You agree not to:</p>
                  <ul>
                    <li>Use the Platform for any unlawful purpose or in violation of any applicable laws</li>
                    <li>Upload or transmit viruses, malware, or other malicious code</li>
                    <li>Interfere with or disrupt the Platform's operation or servers</li>
                    <li>Attempt to gain unauthorized access to any part of the Platform</li>
                    <li>Impersonate another person or entity</li>
                    <li>Harass, abuse, or harm other users</li>
                    <li>Scrape, data mine, or extract data without authorization</li>
                    <li>Use the Platform for spam or unsolicited communications</li>
                  </ul>
                  <h3>5. Intellectual Property Rights</h3>
                  <p><strong>5.1 Platform Ownership:</strong> The Platform, including its design, features, functionality, and content, is owned by Rialty.ai and protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create derivative works without our express written permission.</p>
                  <p><strong>5.2 User Content:</strong> You retain ownership of content you upload to the Platform. By uploading User Content, you grant us a non-exclusive, worldwide, royalty-free license to use, store, display, and process such content solely for the purpose of providing and improving our services.</p>
                  <h3>6. Transaction Management and Compliance</h3>
                  <p>The Platform facilitates real estate transactions but does not provide legal, financial, or professional advice. You are responsible for ensuring compliance with all applicable real estate laws, regulations, and professional standards.</p>
                  <h3>7. Fees and Payments</h3>
                  <p>Certain features of the Platform may require payment of fees. All fees are non-refundable unless otherwise stated. We reserve the right to modify our pricing structure at any time with reasonable notice to users.</p>
                  <h3>8. Data and Privacy</h3>
                  <p>Your use of the Platform is subject to our Privacy Policy, which describes how we collect, use, and protect your information. By using the Platform, you consent to our data practices as described in the Privacy Policy.</p>
                  <h3>9. Disclaimers and Limitation of Liability</h3>
                  <p>The Platform is provided "as is" and "as available" without warranties of any kind. To the maximum extent permitted by law, Rialty.ai shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use or inability to use the Platform.</p>
                  <h3>10. Indemnification</h3>
                  <p>You agree to indemnify, defend, and hold harmless Rialty.ai, its affiliates, and their respective officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses arising from your use of the Platform or violation of these Terms.</p>
                  <h3>11. Termination</h3>
                  <p>We reserve the right to suspend or terminate your account and access to the Platform at any time, with or without cause or notice, including if we believe you have violated these Terms.</p>
                  <h3>12. Changes to Terms</h3>
                  <p>We may modify these Terms at any time. We will provide notice of material changes by posting the updated Terms on the Platform. Your continued use of the Platform after changes constitutes acceptance of the modified Terms.</p>
                  <h3>13. Governing Law</h3>
                  <p>These Terms shall be governed by and construed in accordance with applicable laws of the United States. Any disputes shall be resolved through binding arbitration, except where prohibited by law.</p>
                  <h3>14. Contact Information</h3>
                  <p>If you have questions about these Terms, please contact us at <strong>legal@rialty.ai</strong>.</p>
                </>
              ) : (
                <>
                  <p className="reg-legal-date">Last Updated: November 25, 2025</p>
                  <h3>1. Introduction</h3>
                  <p>Welcome to Rialty.ai ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.</p>
                  <h3>2. Information We Collect</h3>
                  <p><strong>2.1 Personal Information:</strong> We may collect the following types of personal information:</p>
                  <ul>
                    <li>Name, email address, phone number, and contact details</li>
                    <li>Professional information (real estate license number, brokerage affiliation)</li>
                    <li>Account credentials and authentication information</li>
                    <li>Transaction and property-related data</li>
                    <li>Communication and correspondence records</li>
                  </ul>
                  <p><strong>2.2 Automatically Collected Information:</strong> When you use our platform, we automatically collect:</p>
                  <ul>
                    <li>Device information (IP address, browser type, operating system)</li>
                    <li>Usage data (pages viewed, features used, time spent on platform)</li>
                    <li>Cookies and similar tracking technologies</li>
                    <li>Log data and analytics information</li>
                  </ul>
                  <h3>3. How We Use Your Information</h3>
                  <ul>
                    <li>To provide, maintain, and improve our services</li>
                    <li>To process transactions and manage real estate workflows</li>
                    <li>To communicate with you about your account and transactions</li>
                    <li>To send notifications, updates, and administrative messages</li>
                    <li>To detect, prevent, and address fraud or security issues</li>
                    <li>To comply with legal obligations and enforce our terms</li>
                    <li>To analyze usage patterns and improve user experience</li>
                  </ul>
                  <h3>4. Information Sharing and Disclosure</h3>
                  <p>We may share your information with:</p>
                  <ul>
                    <li><strong>Transaction Participants:</strong> Other parties involved in your real estate transactions</li>
                    <li><strong>Service Providers:</strong> Third-party vendors who assist in operating our platform</li>
                    <li><strong>Legal Requirements:</strong> When required by law, court order, or government regulations</li>
                    <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales</li>
                  </ul>
                  <p>We do not sell your personal information to third parties.</p>
                  <h3>5. Data Security</h3>
                  <p>We implement industry-standard security measures to protect your information, including encryption, secure servers, and access controls. However, no method of transmission over the internet is 100% secure.</p>
                  <h3>6. Your Rights and Choices</h3>
                  <ul>
                    <li>Access, update, or delete your personal information</li>
                    <li>Opt-out of marketing communications</li>
                    <li>Disable cookies through your browser settings</li>
                    <li>Request a copy of your data</li>
                    <li>Withdraw consent for data processing (where applicable)</li>
                  </ul>
                  <h3>7. Data Retention</h3>
                  <p>We retain your information for as long as necessary to provide our services, comply with legal obligations, resolve disputes, and enforce our agreements. Transaction data may be retained for regulatory compliance purposes.</p>
                  <h3>8. Children's Privacy</h3>
                  <p>Our platform is not intended for individuals under the age of 18. We do not knowingly collect personal information from children.</p>
                  <h3>9. International Data Transfers</h3>
                  <p>Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your information.</p>
                  <h3>10. Changes to This Privacy Policy</h3>
                  <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on our platform. Your continued use constitutes acceptance.</p>
                  <h3>11. Contact Us</h3>
                  <p>If you have any questions about this Privacy Policy, please contact us at <strong>privacy@rialty.ai</strong>.</p>
                </>
              )}
            </div>

            {/* Footer with agree button */}
            <div className="reg-legal-footer">
              <button
                type="button"
                className="reg-legal-agree-btn"
                onClick={() => { setAgreedToTerms(true); setLegalModal(null); }}
              >
                I have read and agree
              </button>
              <button
                type="button"
                className="reg-legal-dismiss-btn"
                onClick={() => setLegalModal(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterPage;
